import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { ListContent } from '@components/Feed/ListViews/components/ListContent';
import { ListMinimalUser } from '@components/Feed/ListViews/components/ListMinimalUser';
import SingleQuantity from '@components/Feed/ListViews/components/SingleQuantity';
import { ToggledAvatar } from '@components/Feed/ListViews/components/ToggledAvatar';
import {
  FeedListViewContainer,
  FeedListViewSubText,
  FeedListViewWrapper,
} from '@components/Feed/ListViews/components/shared';
import { formatTierNumber } from '@components/Feed/shared';
import BadgeImage from '@components/Tiers/BadgeImage';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, ReleaseFeedPrimarySaleFragmentDoc } from '@graphql/reactQuery';
import { MinimalUserFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import { TIER_NUMBERS, TIERS } from '@utils/tiers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseFeedPrimarySale on PrimarySale {
    id
    user {
      id
      avatar {
        id
        url
      }
      ...MinimalUser
    }
    amountPaidInWei
    quantity
    fromTokenId
    fromSerialNumber
    tierNumber
    date
    toTokenId
    toSerialNumber
    release {
      id
      goldenEggNft {
        id
        tokenId
      }
    }
  }
`);

export const PrimarySaleList = ({
  activityFrag,
}: {
  activityFrag: FragmentType<ReleaseFeedPrimarySaleFragmentDoc>;
}) => {
  const { laptop, desktop } = MediaQuery.useContainer();
  const forceMobileView = laptop && !desktop;

  const {
    date,
    release,
    user,
    fromTokenId,
    fromSerialNumber,
    toTokenId,
    quantity,
    amountPaidInWei,
    tierNumber,
  } = getFragment(ReleaseFeedPrimarySaleFragmentDoc, activityFrag);

  const goldenEgg =
    !!release.goldenEggNft?.tokenId &&
    BigInt(release.goldenEggNft.tokenId) >= fromTokenId &&
    BigInt(release.goldenEggNft.tokenId) <= toTokenId;

  const {
    avatar,
    username,
    publicAddress,
    webappUri: userWebappUri,
  } = getFragment(MinimalUserFragmentDoc, user);

  const price = formatWeiToEthDisplayFree({ weiValue: amountPaidInWei, maxDecimals: 4 });

  const editionLabel = formatTierNumber(tierNumber);

  return (
    <FeedListViewContainer>
      <ToggledAvatar
        src={avatar?.url}
        username={username}
        walletAddress={publicAddress}
        userWebappUri={userWebappUri}
        forceMobileView={forceMobileView}
      />

      <ListContent isoDateString={date} truncateChildren forceMobileView={forceMobileView}>
        <FeedListViewWrapper margin="first">
          <ListMinimalUser userFrag={user} forceMobileView={forceMobileView} />
        </FeedListViewWrapper>
        <FeedListViewSubText>
          minted
          {SPACE}
        </FeedListViewSubText>
        {quantity === 1 && (
          <SingleQuantity
            tierNumber={tierNumber}
            edition={fromSerialNumber ?? fromTokenId}
            goldenEgg={goldenEgg}
          />
        )}
        {quantity > 1 && (
          <FeedListViewSubText>
            <div className="flex items-center">
              {tierNumber === TIER_NUMBERS.LIMITED && (
                <>
                  <BadgeImage type={TIERS.LIMITED} />
                  {SPACE}
                </>
              )}
              {quantity} {editionLabel}Editions{SPACE}
            </div>
          </FeedListViewSubText>
        )}
        <FeedListViewSubText>for{SPACE}</FeedListViewSubText>
        <FeedListViewSubText bold>{price}</FeedListViewSubText>
      </ListContent>
    </FeedListViewContainer>
  );
};
